import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  QrCode,
  Camera,
  Upload,
  Loader2,
  Building2,
  Wrench,
  AlertTriangle,
  CheckCircle2,
  X,
  ImageIcon
} from "lucide-react";
import { toast } from "sonner";
import { Html5QrcodeScanner } from "html5-qrcode";

const QRScanner = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [scannedClinic, setScannedClinic] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [images, setImages] = useState([]);
  const [formData, setFormData] = useState({
    element_type: "",
    category: "",
    description: "",
    priority: "medium"
  });
  const scannerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        scannerRef.current.clear().catch(console.error);
      }
    };
  }, []);

  const startScanner = () => {
    setScanning(true);
    setTimeout(() => {
      const scanner = new Html5QrcodeScanner("qr-reader", {
        qrbox: { width: 250, height: 250 },
        fps: 5,
      });

      scanner.render(onScanSuccess, onScanError);
      scannerRef.current = scanner;
    }, 100);
  };

  const stopScanner = () => {
    if (scannerRef.current) {
      scannerRef.current.clear().catch(console.error);
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const onScanSuccess = async (decodedText) => {
    stopScanner();
    
    // Parse clinic ID from QR code (format: "clinic:uuid")
    const match = decodedText.match(/^clinic:(.+)$/);
    if (!match) {
      toast.error("رمز QR غير صالح");
      return;
    }

    const clinicId = match[1];
    setLoading(true);

    try {
      const response = await axios.get(`${API}/clinics/${clinicId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScannedClinic(response.data);
      toast.success(`تم التعرف على ${response.data.name}`);
    } catch (error) {
      toast.error("العيادة غير موجودة");
    } finally {
      setLoading(false);
    }
  };

  const onScanError = (error) => {
    // Ignore scan errors (they happen continuously until QR is found)
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length + images.length > 5) {
      toast.error("الحد الأقصى 5 صور");
      return;
    }

    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);

      try {
        const response = await axios.post(`${API}/upload`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        });
        setImages(prev => [...prev, response.data.data]);
      } catch (error) {
        toast.error("فشل في رفع الصورة");
      }
    }
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!scannedClinic || !formData.element_type || !formData.category || !formData.description) {
      toast.error("يرجى ملء جميع الحقول المطلوبة");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/maintenance`, {
        clinic_id: scannedClinic.id,
        element_type: formData.element_type,
        category: formData.category,
        description: formData.description,
        priority: formData.priority,
        images: images
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("تم إرسال طلب الصيانة بنجاح");
      navigate("/maintenance");
    } catch (error) {
      toast.error("فشل في إرسال الطلب");
    } finally {
      setSubmitting(false);
    }
  };

  const structuralCategories = [
    { value: "wall", label: "الجدران" },
    { value: "floor", label: "الأرضيات" },
    { value: "ceiling", label: "السقف" },
    { value: "lighting", label: "الإضاءة" },
    { value: "plumbing", label: "السباكة" },
    { value: "electrical", label: "الكهرباء" },
    { value: "other", label: "أخرى" }
  ];

  const deviceCategories = [
    { value: "dental_chair", label: "كرسي الأسنان" },
    { value: "xray", label: "جهاز الأشعة" },
    { value: "sterilizer", label: "جهاز التعقيم" },
    { value: "compressor", label: "الضاغط" },
    { value: "suction", label: "جهاز الشفط" },
    { value: "other", label: "أخرى" }
  ];

  const categories = formData.element_type === "structural" ? structuralCategories : deviceCategories;

  return (
    <div className="space-y-6" data-testid="qr-scanner-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#655dc6]">الإبلاغ عن صيانة</h1>
        <p className="text-slate-500 mt-1">امسح رمز QR الخاص بالعيادة للإبلاغ عن مشكلة</p>
      </div>

      {!scannedClinic ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-12">
            {!scanning ? (
              <div className="text-center">
                <div className="w-24 h-24 rounded-2xl bg-[#F5F3F9] flex items-center justify-center mx-auto mb-6">
                  <QrCode className="w-12 h-12 text-[#655dc6]" />
                </div>
                <h3 className="text-xl font-semibold text-[#655dc6] mb-2">مسح رمز QR</h3>
                <p className="text-slate-500 mb-6 max-w-md mx-auto">
                  قم بمسح رمز QR الموجود داخل العيادة لتحديد موقعك وتقديم طلب صيانة
                </p>
                <Button
                  onClick={startScanner}
                  className="bg-[#655dc6] hover:bg-[#5449a8] text-white"
                  data-testid="start-scan-btn"
                >
                  <Camera className="w-5 h-5 ml-2" />
                  بدء المسح
                </Button>
              </div>
            ) : (
              <div className="text-center">
                <div id="qr-reader" className="mb-4"></div>
                {loading && (
                  <div className="flex items-center justify-center gap-2 text-[#655dc6]">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>جاري البحث عن العيادة...</span>
                  </div>
                )}
                <Button
                  variant="outline"
                  onClick={stopScanner}
                  className="mt-4"
                  data-testid="stop-scan-btn"
                >
                  إلغاء المسح
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Scanned Clinic Info */}
          <Card className="border-0 shadow-sm border-r-4 border-r-[#9b8fd4]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-[#F5F3F9] flex items-center justify-center">
                  <Building2 className="w-7 h-7 text-[#655dc6]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-lg text-[#655dc6]">{scannedClinic.name}</h3>
                  <p className="text-slate-500">{scannedClinic.floor} • {scannedClinic.building}</p>
                </div>
                <CheckCircle2 className="w-6 h-6 text-emerald-500" />
              </div>
            </CardContent>
          </Card>

          {/* Maintenance Request Form */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg text-[#655dc6] flex items-center gap-2">
                <Wrench className="w-5 h-5 text-[#9b8fd4]" />
                تفاصيل طلب الصيانة
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>نوع العنصر *</Label>
                    <Select
                      value={formData.element_type}
                      onValueChange={(v) => setFormData({ ...formData, element_type: v, category: "" })}
                    >
                      <SelectTrigger data-testid="element-type-select">
                        <SelectValue placeholder="اختر نوع العنصر" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="structural">عنصر إنشائي</SelectItem>
                        <SelectItem value="device">جهاز طبي</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>الفئة *</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(v) => setFormData({ ...formData, category: v })}
                      disabled={!formData.element_type}
                    >
                      <SelectTrigger data-testid="category-select">
                        <SelectValue placeholder="اختر الفئة" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>الأولوية *</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(v) => setFormData({ ...formData, priority: v })}
                  >
                    <SelectTrigger data-testid="priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">منخفضة</SelectItem>
                      <SelectItem value="medium">متوسطة</SelectItem>
                      <SelectItem value="high">عالية</SelectItem>
                      <SelectItem value="urgent">عاجلة</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>وصف المشكلة *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="صف المشكلة بالتفصيل..."
                    className="text-right min-h-[120px]"
                    data-testid="description-textarea"
                  />
                </div>

                <div className="space-y-2">
                  <Label>إضافة صور (اختياري)</Label>
                  <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-[#9b8fd4] transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                      id="image-upload"
                      data-testid="image-upload-input"
                    />
                    <label htmlFor="image-upload" className="cursor-pointer">
                      <Upload className="w-10 h-10 mx-auto mb-3 text-slate-400" />
                      <p className="text-slate-600 mb-1">اضغط لرفع الصور</p>
                      <p className="text-sm text-slate-400">الحد الأقصى 5 صور</p>
                    </label>
                  </div>

                  {images.length > 0 && (
                    <div className="flex flex-wrap gap-3 mt-4">
                      {images.map((img, index) => (
                        <div key={index} className="relative">
                          <img
                            src={img}
                            alt={`Uploaded ${index + 1}`}
                            className="w-20 h-20 object-cover rounded-lg border"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    className="flex-1 bg-[#655dc6] hover:bg-[#5449a8] py-6 text-white"
                    disabled={submitting}
                    data-testid="submit-request-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin ml-2" />
                        جاري الإرسال...
                      </>
                    ) : (
                      <>
                        <Wrench className="w-5 h-5 ml-2" />
                        إرسال طلب الصيانة
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setScannedClinic(null)}
                    className="py-6"
                  >
                    مسح عيادة أخرى
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default QRScanner;
